import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import DisclaimerInfo from '../components/DisclaimerInfo';
import { useUserEvent } from '../hooks';
import { useFavoritos } from '../hooks/useFavoritos';
import { supabase } from '../supabaseClient';
import { FaBell, FaChartLine, FaClock, FaCloudRain, FaCloudSun, FaCloudSunRain, FaFileExcel, FaFilePdf, FaMapMarkerAlt, FaSearch, FaSeedling, FaTable, FaTemperatureHigh, FaTint, FaWind } from 'react-icons/fa';
import { ThemeContext } from '../App';
import { useToast } from '../components/Toast';
import { MunicipioSelector } from '../components/MunicipioSelector';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Importação condicional do Capacitor Filesystem
let Filesystem, Directory;
if (window && window.Capacitor) {
  try {
    // eslint-disable-next-line
    ({ Filesystem, Directory } = require('@capacitor/filesystem'));
  } catch (e) {
    // Ignora se não estiver disponível
  }
}

import Button from './Button';
import { ResumoCard } from './ResumoCard';
import { TemperaturasChart, PrecipitacaoChart, EtoChart, UmidadeChart } from './Charts';
import { LoadingSkeleton, TableSkeleton } from '../components/LoadingSkeleton';
import { variaveisDisponiveis } from '../constants/variaveis';
import { usePlanLimits } from '../hooks/usePlanLimits';
import UpgradeModal from '../components/UpgradeModal';
import ForecastOperationalOverview from './ForecastOperationalOverview';
import HourlyForecast from './HourlyForecast';
import {
  currentForecastDateKey,
  forecastDateKey,
  formatForecastDate,
  normalizeCurrentForecast,
} from './forecastOperationalService';
import '../styles/module-shell.css';
import '../styles/tech-components.css';
import './PrevisaoClimatica.css';

// Fonte disponível para a previsão diária; a consulta horária também usa GFS.
const MODELO_PREVISAO = {
  value: 'gfs', label: 'GFS', icone: '🛰️', tabela: 'climate_forecast_data',
  descricao: 'Modelo determinístico (NOAA)',
};

function useIsMobile(bp = 768) {
  const [v, setV] = React.useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  React.useEffect(() => {
    const h = () => setV(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return v;
}

export default function PrevisaoClimatica({
  onBack,
  user,
  previewMode = false,
  previewData,
  previewHourlyData,
  previewMunicipios = [],
  previewInitialMunicipio = null,
  previewOperational = false,
  onNavigate,
  operationalProperties = [],
}) {
  useUserEvent({ userId: user?.id, tela: 'PrevisaoClimatica', evento: 'page_view', comDuração: true });
  const isMobile = useIsMobile();

  const { darkMode } = useContext(ThemeContext);
  const toast = useToast();

  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(!previewMode);
  const [hourlyData, setHourlyData] = useState([]);
  const [hourlyLoading, setHourlyLoading] = useState(false);
  const [hourlyError, setHourlyError] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('resumo');
  const [selectedMunicipio, setSelectedMunicipio] = useState(previewInitialMunicipio);
  const autoSelecionadoRef = useRef(Boolean(previewInitialMunicipio));
  const selecaoManualRef = useRef(false);

  useEffect(() => {
    if (!previewMode) return;
    setSelectedMunicipio(previewInitialMunicipio || null);
    autoSelecionadoRef.current = Boolean(previewInitialMunicipio);
  }, [previewMode, previewInitialMunicipio]);

  // Hook de favoritos
  const { 
    favoritos,
    loading: favoritosLoading,
    addFavorito, 
    removeFavorito, 
    isFavorito 
  } = useFavoritos(user?.id);

  // Prioriza o município de uma propriedade real. Favoritos só são usados
  // quando a conta ainda não possui contexto territorial.
  useEffect(() => {
    if (previewMode || selecaoManualRef.current) return;
    const municipioPropriedade = operationalProperties.find(property => property?.municipio)?.municipio;
    if (municipioPropriedade) {
      if (selectedMunicipio !== municipioPropriedade) setSelectedMunicipio(municipioPropriedade);
      autoSelecionadoRef.current = true;
      return;
    }
    if (autoSelecionadoRef.current) return;
    if (favoritosLoading) return;
    autoSelecionadoRef.current = true;
    if (favoritos.length > 0) {
      setSelectedMunicipio(favoritos[0].name_muni);
    }
    // Sem favoritos: deixa null para exibir tela de seleção
  }, [favoritos, favoritosLoading, operationalProperties, previewMode, selectedMunicipio]);

  // Sistema de Planos
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { podeUsar } = usePlanLimits(user?.id);

  const carregarDados = useCallback(async () => {
    if (!selectedMunicipio) {
      setLoading(false);
      if (previewMode) setDados([]);
      return;
    }

    if (previewMode) {
      setDados(normalizeCurrentForecast(previewData || []));
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from(MODELO_PREVISAO.tabela)
        .select('*')
        .eq('name_muni', selectedMunicipio)
        .order('date', { ascending: true });

      if (error) throw error;
      
      setDados(normalizeCurrentForecast(data || []));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados: ' + error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMunicipio, toast, previewMode, previewData]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    let isCurrentRequest = true;

    async function carregarDadosHorarios() {
      if (abaAtiva !== 'horaria' || !selectedMunicipio) return;

      setHourlyLoading(true);
      setHourlyError(null);

      if (previewMode) {
        setHourlyData(previewHourlyData || []);
        setHourlyLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('climate_forecast_hourly_data')
          .select('datetime_utc,TMP,RH,APCP,WS2M,DSWRF,PRES')
          .eq('name_muni', selectedMunicipio)
          .gte('datetime_utc', new Date().toISOString())
          .order('datetime_utc', { ascending: true })
          .limit(240);

        if (error) throw error;
        if (isCurrentRequest) setHourlyData(data || []);
      } catch (error) {
        console.error('Erro ao carregar previsão horária:', error);
        if (isCurrentRequest) {
          setHourlyData([]);
          setHourlyError(error.message || 'Tente novamente mais tarde.');
        }
      } finally {
        if (isCurrentRequest) setHourlyLoading(false);
      }
    }

    carregarDadosHorarios();
    return () => { isCurrentRequest = false; };
  }, [abaAtiva, selectedMunicipio, previewMode, previewHourlyData]);

  // Handler para trocar município
  const handleMunicipioChange = useCallback((nomeMunicipio, municipioObj) => {
    selecaoManualRef.current = true;
    setSelectedMunicipio(nomeMunicipio);
  }, []);

  // Handler para toggle de favorito
  const handleToggleFavorito = useCallback(async (municipio) => {
    if (previewMode) return;
    const nomeMuni = typeof municipio === 'string' ? municipio : municipio.name_muni;
    
    if (isFavorito(nomeMuni)) {
      const result = await removeFavorito(nomeMuni);
      if (result.success) {
        toast.success('Removido dos favoritos');
      } else {
        toast.error('Erro ao remover favorito');
      }
    } else {
      const municipioObj = typeof municipio === 'string' 
        ? { name_muni: municipio }
        : municipio;
      
      const result = await addFavorito(municipioObj);
      if (result.success) {
        toast.success('Adicionado aos favoritos!');
      } else {
        toast.error('Erro ao adicionar favorito');
      }
    }
  }, [isFavorito, addFavorito, removeFavorito, toast, previewMode]);

  const formatDate = (dateStr) => {
    return formatForecastDate(dateStr);
  };

  // Detecta se é ambiente Capacitor
  const isCapacitor = useMemo(() => {
    try {
      return window && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  }, []);


  // Exportar XLSX compatível com web e mobile
  async function exportarXLSX() {
    // ⚠️ VERIFICAR SE O PLANO PERMITE DOWNLOAD
    if (!podeUsar('download_previsao')) {
      setShowUpgradeModal(true);
      return; // PARA AQUI - Plano não permite!
    }

    try {
      const ws = XLSX.utils.json_to_sheet(dados);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Previsao');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileName = `previsao_climatica_${MODELO_PREVISAO.value}.xlsx`;

      if (isCapacitor && Filesystem && Directory) {
        // Converte array buffer para base64
        function arrayBufferToBase64(buffer) {
          let binary = '';
          const bytes = new Uint8Array(buffer);
          const len = bytes.byteLength;
          for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          return window.btoa(binary);
        }
        const xlsxBase64 = arrayBufferToBase64(wbout);
        await Filesystem.writeFile({
          path: fileName,
          data: xlsxBase64,
          directory: Directory.Documents,
        });
        toast.success('XLSX salvo em Documentos!');
      } else {
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        saveAs(blob, fileName);
        toast.success('XLSX baixado com sucesso!');
      }
    } catch (err) {
      console.error('Erro ao exportar XLSX:', err);
      toast.error('Erro ao exportar XLSX: ' + (err?.message || 'Erro desconhecido'));
    }
  }


  // Exportar PDF compatível com web e mobile
  async function exportarPDF() {
    // ⚠️ VERIFICAR SE O PLANO PERMITE DOWNLOAD
    if (!podeUsar('download_previsao')) {
      setShowUpgradeModal(true);
      return; // PARA AQUI - Plano não permite!
    }

    try {
      // Carrega o logo como base64
      async function getLogoBase64() {
        const response = await fetch('/ClimoraX_vertical.png');
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }

      const doc = new jsPDF();
      const logoBase64 = await getLogoBase64();
      
      // Cabeçalho compacto com logo vertical
      doc.addImage(logoBase64, 'PNG', 15, 8, 28, 28);
      doc.setFontSize(14);
      doc.setTextColor(0, 59, 115);
      doc.text('Relatório de Previsão Climática', 46, 20);
      doc.setFontSize(9);
      doc.setTextColor(119, 119, 119);
      doc.text(new Date().toLocaleDateString('pt-BR'), 46, 27);

      // Informações do município
      let infoY = 40;
      doc.setFontSize(10);
      doc.setTextColor(34, 203, 34);
      doc.text('Município:', 15, infoY);
      doc.setTextColor(34, 34, 34);
      doc.text(selectedMunicipio || '-', 40, infoY);
      infoY += 6;

      // Tabela de dados
      autoTable(doc, {
        head: [['Data', ...variaveisDisponiveis.map(v => v.label)]],
        body: dados.map(linha => [
          formatDate(linha.date),
          ...variaveisDisponiveis.map(v => linha[v.value] ?? '-')
        ]),
        startY: infoY + 4,
        styles: { fontSize: 8 },
      });
      
      const fileName = `previsao_climatica_${MODELO_PREVISAO.value}.pdf`;

      if (isCapacitor && Filesystem && Directory) {
        const pdfBase64 = doc.output('datauristring').split(',')[1];
        await Filesystem.writeFile({
          path: fileName,
          data: pdfBase64,
          directory: Directory.Documents,
        });
        toast.success('PDF salvo em Documentos!');
      } else {
        doc.save(fileName);
        toast.success('PDF baixado com sucesso!');
      }
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
      toast.error('Erro ao exportar PDF: ' + (err?.message || 'Erro desconhecido'));
    }
  }

  // Calcula previsão para hoje com memoização
  const previsaoHoje = useMemo(() => {
    const todayKey = currentForecastDateKey();
    return dados.find(item => forecastDateKey(item.date) === todayKey);
  }, [dados]);

  return (
    <div className={`module-shell module-shell-previsao ${darkMode ? 'is-dark' : ''}`} style={{
      minHeight: '100vh',
      background: 'transparent',
      color: darkMode ? '#f8fafc' : undefined,
      paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingRight: `calc(env(safe-area-inset-right, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${isMobile ? '80px' : '24px'})`,
      fontFamily: 'var(--font-body)',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div className="module-topbar" style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        marginBottom: 16,
        marginLeft: `calc(-1 * (env(safe-area-inset-left, 0px) + ${isMobile ? '12px' : '24px'}))`,
        marginRight: `calc(-1 * (env(safe-area-inset-right, 0px) + ${isMobile ? '12px' : '24px'}))`,
        paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${isMobile ? '12px' : '24px'})`,
        paddingRight: `calc(env(safe-area-inset-right, 0px) + ${isMobile ? '12px' : '24px'})`,
        paddingTop: isMobile ? 10 : 14,
        paddingBottom: isMobile ? 10 : 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
          <button
            onClick={onBack}
            style={{
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '6px 4px',
              minWidth: 44,
              minHeight: 44,
              color: darkMode ? '#60a5fa' : '#0284c7',
              fontSize: isMobile ? 13 : 14,
              fontWeight: 600,
              borderRadius: 6,
              transition: 'opacity 0.15s',
              whiteSpace: 'nowrap',
              touchAction: 'manipulation'
            }}
            aria-label="Voltar ao painel"
            onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            {!isMobile && <span>Voltar</span>}
          </button>
          <div style={{ width: 1, height: isMobile ? 28 : 32, background: darkMode ? '#374151' : '#e2e8f0', flexShrink: 0 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, minWidth: 0 }}>
            <div style={{
              width: isMobile ? 34 : 40,
              height: isMobile ? 34 : 40,
              borderRadius: 11,
              background: 'linear-gradient(135deg, #1976d2, #43cea2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? 16 : 20,
              boxShadow: '0 3px 10px rgba(25,118,210,0.30)',
              flexShrink: 0
            }}>🌦️</div>
            <div style={{ minWidth: 0 }}>
              <h1 className="module-title" style={{
                fontSize: isMobile ? 16 : 20,
                fontWeight: 700,
                color: darkMode ? '#f1f5f9' : '#0f172a',
                margin: 0,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>Previsão Climática</h1>
              <p style={{
                margin: 0,
                fontSize: 11,
                color: darkMode ? '#6b7280' : '#94a3b8',
                marginTop: 2,
                whiteSpace: 'nowrap'
              }}>{selectedMunicipio || 'Selecione um município'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={`tech-panel forecast-toolbar ${!selectedMunicipio ? 'forecast-empty-state' : ''}`} style={{ marginBottom: '2rem', padding: isMobile ? '0.9rem 0.7rem' : '1rem' }}>
        {!selectedMunicipio && favoritosLoading && (
          <div className="forecast-initial-loading" aria-label="Preparando busca de municípios">
            <LoadingSkeleton darkMode={darkMode} />
          </div>
        )}
        {!selectedMunicipio && !favoritosLoading && (
          <div className="forecast-empty-layout" aria-labelledby="forecast-empty-title">
            <div className="forecast-empty-main">
              <span className="forecast-empty-kicker">Planejamento agrometeorológico</span>
              <div className="forecast-empty-icon" aria-hidden="true"><FaCloudSunRain /></div>
              <h2 id="forecast-empty-title">Consulte o clima antes de decidir no campo</h2>
              <p>
                Escolha um município para visualizar temperatura, chuva, umidade, vento e
                evapotranspiração em uma única análise.
              </p>
              <div className="forecast-search-box">
                <div className="forecast-search-heading">
                  <FaSearch aria-hidden="true" />
                  <div><strong>Buscar município</strong><span>Digite pelo menos duas letras do nome.</span></div>
                </div>
                <MunicipioSelector
                  value={selectedMunicipio}
                  onChange={handleMunicipioChange}
                  darkMode={darkMode}
                  favoritos={favoritos}
                  onToggleFavorito={previewMode ? undefined : handleToggleFavorito}
                  isFavorito={previewMode ? undefined : isFavorito}
                  previewMode={previewMode}
                  previewMunicipios={previewMunicipios}
                />
              </div>
              <span className="forecast-empty-note"><FaMapMarkerAlt aria-hidden="true" /> Você poderá trocar de local ou salvar favoritos quando quiser.</span>
            </div>
            <aside className="forecast-empty-benefits" aria-label="Informações disponíveis na previsão">
              <span>Depois da seleção</span>
              <h3>Informações para apoiar o manejo</h3>
              <div className="forecast-empty-benefit-grid">
                <div><span><FaCloudSun aria-hidden="true" /></span><strong>Condições do dia</strong><p>Temperaturas, umidade e vento.</p></div>
                <div><span><FaCloudRain aria-hidden="true" /></span><strong>Chuva prevista</strong><p>Volumes diários e tendência.</p></div>
                <div><span><FaSeedling aria-hidden="true" /></span><strong>Demanda hídrica</strong><p>ETo para apoiar o planejamento.</p></div>
                <div><span><FaBell aria-hidden="true" /></span><strong>Alertas climáticos</strong><p>Acompanhe condições importantes.</p></div>
              </div>
            </aside>
          </div>
        )}
        {selectedMunicipio && (
        <div className="forecast-location-controls">
        <div className="forecast-location-selector">
        <MunicipioSelector
          value={selectedMunicipio}
          onChange={handleMunicipioChange}
          darkMode={darkMode}
          favoritos={favoritos}
          onToggleFavorito={previewMode ? undefined : handleToggleFavorito}
          isFavorito={previewMode ? undefined : isFavorito}
          previewMode={previewMode}
          previewMunicipios={previewMunicipios}
        />
        </div>
            <div className="forecast-model-source" aria-label="Fonte da previsão">
              <span>Fonte:</span>
              <span
                title={MODELO_PREVISAO.descricao}
              >
                <span aria-hidden="true">{MODELO_PREVISAO.icone}</span> {MODELO_PREVISAO.label}
              </span>
            </div>
        </div>)}
        <div className="forecast-disclaimer">
          <DisclaimerInfo tipo="previsao" />
        </div>
      </div>

      {(previewOperational || !previewMode) && selectedMunicipio && !loading && dados.length > 0 && (
        <ForecastOperationalOverview
          dados={dados}
          municipio={selectedMunicipio}
          onShowHourly={() => setAbaAtiva('horaria')}
          properties={operationalProperties}
          modelLabel={MODELO_PREVISAO.label}
        />
      )}

      {/* Abas e conteúdo */}
      {selectedMunicipio && (<>
      <div className="tech-tabbar forecast-view-tabs has-hourly" style={{ justifyContent: isMobile ? 'flex-start' : 'center', marginBottom: isMobile ? 12 : 24 }}>
        <button
          className={`tech-tab-btn ${abaAtiva === 'resumo' ? 'is-active' : ''}`}
          onClick={() => setAbaAtiva('resumo')}
          style={{
            whiteSpace: 'nowrap',
            minWidth: isMobile ? 108 : 140
          }}
        >
          <FaChartLine aria-hidden="true" /> Resumo
        </button>
        <button
          className={`tech-tab-btn ${abaAtiva === 'tabela' ? 'is-active' : ''}`}
          onClick={() => setAbaAtiva('tabela')}
          style={{
            whiteSpace: 'nowrap',
            minWidth: isMobile ? 108 : 140
          }}
        >
          <FaTable aria-hidden="true" /> {isMobile ? 'Tabela' : 'Tabela de dados'}
        </button>
          <button
            className={`tech-tab-btn ${abaAtiva === 'horaria' ? 'is-active' : ''}`}
            onClick={() => setAbaAtiva('horaria')}
            style={{ whiteSpace: 'nowrap', minWidth: isMobile ? 108 : 140 }}
          >
            <FaClock aria-hidden="true" /> Por hora
          </button>
        <button
          className="tech-tab-btn"
          onClick={() => onNavigate?.('alertas', { source: 'previsao' })}
          style={{
            whiteSpace: 'nowrap',
            minWidth: isMobile ? 142 : 170
          }}
        >
          <FaBell aria-hidden="true" /> Central de Alertas
        </button>
      </div>

      {/* Conteúdo das abas */}
      {abaAtiva === 'resumo' && (
        <>
          {loading ? (
            <LoadingSkeleton darkMode={darkMode} />
          ) : dados.length === 0 ? (
            <p style={{ textAlign: 'center', fontSize: 20, color: '#d32f2f' }}>
              Nenhum dado disponível para o município selecionado.
            </p>
          ) : (
            <>
              {previsaoHoje && (
                <section className="forecast-current-summary" aria-label="Condições previstas para hoje" style={{
                  display: isMobile ? 'grid' : 'flex',
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : undefined,
                  justifyContent: 'center',
                  gap: isMobile ? '0.5rem' : '2.5rem',
                  flexWrap: 'wrap',
                  padding: isMobile ? '0 0.75rem' : '0 2rem',
                  maxWidth: isMobile ? '100%' : '1300px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  marginTop: 0,
                  marginBottom: isMobile ? '1.5rem' : '4rem',
                  boxSizing: 'border-box'
                }}>
                  <div className="forecast-summary-heading">
                    <div><span>Condições atuais</span><h2>Indicadores de hoje</h2></div>
                    <p>Resumo calculado a partir da previsão GFS.</p>
                  </div>
                  <ResumoCard 
                    label="Temp. Média" 
                    value={(previsaoHoje.tmean ?? '-') + '°C'} 
                    icon={<FaTemperatureHigh />}
                    color="#1976d2"
                    isMobile={isMobile}
                  />
                  <ResumoCard 
                    label="Precipitação" 
                    value={(previsaoHoje.ppt ?? '-') + ' mm'} 
                    icon={<FaCloudRain />}
                    color="#43cea2"
                    isMobile={isMobile}
                  />
                  <ResumoCard 
                    label="Umidade" 
                    value={(previsaoHoje.ur ?? '-') + '%'} 
                    icon={<FaTint />}
                    color="#00bcd4"
                    isMobile={isMobile}
                  />
                  <ResumoCard 
                    label="Vento" 
                    value={(previsaoHoje.ws2m ?? '-') + ' m/s'} 
                    icon={<FaWind />}
                    color="#ffa500"
                    isMobile={isMobile}
                  />
                  <ResumoCard 
                    label="ETo" 
                    value={(previsaoHoje.eto_mm_day ?? '-') + ' mm/dia'} 
                    icon={<FaSeedling />}
                    color="#2e9e6e"
                    isMobile={isMobile}
                  />
                </section>
              )}

              {!previsaoHoje && (
                <div style={{ textAlign: 'center', color: '#d32f2f', fontWeight: 600, marginBottom: '2rem' }}>
                  Não há previsão disponível para hoje.
                </div>
              )}

              <div className="forecast-charts-grid">
                <TemperaturasChart dados={dados} formatDate={formatDate} isMobile={isMobile} />
                <PrecipitacaoChart dados={dados} formatDate={formatDate} isMobile={isMobile} />
                <EtoChart dados={dados} formatDate={formatDate} isMobile={isMobile} />
                <UmidadeChart dados={dados} formatDate={formatDate} isMobile={isMobile} />
              </div>
            </>
          )}
        </>
      )}

      {abaAtiva === 'tabela' && (
        <section className="forecast-data-panel" style={{
          maxWidth: 1100, 
          margin: '0 auto', 
          background: darkMode ? '#232526' : 'white', 
          borderRadius: isMobile ? 12 : 18, 
          boxShadow: darkMode ? '0 2px 12px #1118' : '0 2px 12px rgba(25, 118, 210, 0.07)', 
          padding: isMobile ? '1rem 0.75rem' : '2rem 1.5rem', 
          marginBottom: isMobile ? '1rem' : '2rem',
          marginLeft: isMobile ? '0.5rem' : 'auto',
          marginRight: isMobile ? '0.5rem' : 'auto'
        }}>
          <div className="forecast-data-heading">
            <div>
              <span>Previsão detalhada</span>
              <h2>Tabela de dados</h2>
              <p>Consulte todos os indicadores diários ou exporte o período disponível.</p>
            </div>
            <div className="forecast-export-actions">
              <Button
                onClick={exportarXLSX}
                color={darkMode ? '#90caf9' : '#1976d2'}
                textColor={darkMode ? '#232526' : '#fff'}
              >
                <FaFileExcel aria-hidden="true" /> Exportar XLSX
              </Button>
              <Button
                onClick={exportarPDF}
                color={darkMode ? '#80cbc4' : '#43cea2'}
                textColor={darkMode ? '#232526' : '#fff'}
              >
                <FaFilePdf aria-hidden="true" /> Exportar PDF
              </Button>
            </div>
          </div>
          {loading ? (
            <TableSkeleton darkMode={darkMode} />
          ) : (
            <div className="forecast-table-scroll" style={{ overflowX: 'auto', marginTop: '1rem', width: '100%' }}>
              <table className="forecast-data-table" style={{
                borderCollapse: 'collapse',
                width: '100%',
                minWidth: 700,
                fontSize: 15,
                background: darkMode ? '#232526' : 'white',
                borderRadius: 10,
                overflow: 'hidden',
                boxShadow: darkMode ? '0 1px 4px #1118' : '0 1px 4px rgba(25, 118, 210, 0.05)'
              }}>
                <thead style={{ background: darkMode ? '#111' : '#1976d2', color: 'white' }}>
                  <tr>
                    <th style={{ padding: '0.75rem' }}>Data</th>
                    {variaveisDisponiveis.map(v => (
                      <th key={v.value} style={{ padding: '0.75rem' }}>{v.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dados.map((linha, index) => (
                    <tr
                      key={index}
                      style={{
                        background: darkMode
                          ? index % 2 === 0
                            ? '#232526'
                            : '#2d2f31'
                          : index % 2 === 0
                            ? '#f4f8fb'
                            : 'white',
                        color: darkMode ? '#f8fafc' : '#222'
                      }}
                    >
                      <td style={{ padding: '0.75rem' }}>{formatDate(linha.date)}</td>
                      {variaveisDisponiveis.map(v => (
                        <td key={v.value} style={{ padding: '0.75rem' }}>{linha[v.value] ?? '-'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {abaAtiva === 'horaria' && (
        <HourlyForecast
          rows={hourlyData}
          dailyRows={dados}
          loading={hourlyLoading}
          error={hourlyError}
          darkMode={darkMode}
          isMobile={isMobile}
        />
      )}

      </>)}

      {/* Modal de Upgrade de Plano */}
      <UpgradeModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        recurso="download"
        usuario={user}
      />
    </div>
  );
}
